// Auth session store. The access token stays in memory only; the refresh
// token is persisted to **sessionStorage** so an F5 reload can restore the
// session transparently (silent /api/auth/refresh at boot) while keeping
// sessions per-tab: two tabs of the same browser can stay logged in as two
// different users, and a stale tab never resurrects another tab's login.
// Closing the tab/browser ends the stored session; the access token is never
// persisted anywhere.

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { AuthResponse } from "../lib/types";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  userId: string | null;
  username: string | null;
  expiresAt: number | null;
  /**
   * "pending" until App() finishes the boot restore (rehydrating a persisted
   * refresh token, or proving there is nothing to restore). Guards must wait
   * for "ready" before deciding to bounce to login, otherwise a reload with a
   * valid session would flash the login screen. Never persisted.
   */
  bootStatus: "pending" | "ready";
  setSession: (s: AuthResponse) => void;
  markReady: () => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      userId: null,
      username: null,
      expiresAt: null,
      bootStatus: "pending",
      setSession: (s) =>
        set({
          accessToken: s.access_token,
          refreshToken: s.refresh_token,
          userId: s.user_id,
          username: s.username,
          expiresAt: Date.now() + s.expires_in * 1000,
        }),
      markReady: () => set({ bootStatus: "ready" }),
      clear: () =>
        set({ accessToken: null, refreshToken: null, userId: null, username: null, expiresAt: null }),
    }),
    {
      name: "ohre-auth",
      // Per-tab storage: survives F5, isolates concurrent tabs, dies with the
      // tab. Persisted fields: identity + the refresh token (needed to mint a
      // new access token after a reload) — never the access token itself.
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        userId: s.userId,
        username: s.username,
        refreshToken: s.refreshToken,
      }),
    },
  ),
);
